import { Stack, Text, VisuallyHidden } from '@mantine/core';
import { Fragment } from 'react';
import { AudioTranscriptCard } from 'src/common/components/audio-transcript-card/AudioTranscriptCard';
import { useSegmentPlayback } from 'src/common/components/audio-transcript-card/useSegmentPlayback';
import { useDocumentAudio } from 'src/meeting-note/hooks/useDocumentAudio';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { plainQuote } from './plainQuote';
import { VerdictLine } from './VerdictLine';
import { VerifyCallout } from './VerifyCallout';
import classes from './VerifyCallout.module.css';
import { VerifyOffer } from './VerifyOffer';
import {
  ClientRecordEvidence,
  DocumentEvidence,
  TranscriptEvidence,
  VerifyEvidence,
  VerifyOutcome,
} from './types';

type VerifyResultProps = {
  outcome: VerifyOutcome;
  evidence: VerifyEvidence[];
  /** The canned verdict line, built server-side from the primary evidence. */
  headline?: string;
  /** The model reported a match and none of its citations resolved. */
  unresolved?: boolean;
  /** Meeting note (document) id, used to fetch the recording for playback. */
  meetingNoteId: string | undefined;
  /** The model's short spoken answer, shown beneath the verdict line. */
  message?: string;
  /** Sidebar is dark (Siesta chats): use light text against the dark background. */
  dark?: boolean;
  /** Hands the verify context off to the normal document editor pass (correction / not-found). */
  onAction: () => void;
};

export function VerifyResult({
  outcome,
  evidence,
  headline,
  unresolved,
  meetingNoteId,
  message,
  dark,
  onAction,
}: VerifyResultProps) {
  const primary = primaryEvidence(evidence);
  const transcriptItems = primary?.kind === 'transcript' ? primary.items : [];

  // Each recording numbers its own segments, so evidence is played against the one it names.
  const citedFileIds = [
    ...new Set(transcriptItems.flatMap(item => (item.fileId ? [item.fileId] : []))),
  ];
  const audioUrls = useDocumentAudio(
    transcriptItems.length > 0 ? meetingNoteId : undefined,
    citedFileIds,
  );
  const playback = useSegmentPlayback();
  const spokenReply = message?.trim();

  // No evidence anywhere — the only genuine "couldn't verify" outcome.
  if (outcome === 'not_found') {
    return (
      <VerifyCallout
        headline={headline}
        message={spokenReply}
        dark={dark}
        unresolved={unresolved}
        onAction={onAction}
      />
    );
  }

  const differs = outcome === 'correction';
  // A confirmed claim needs no sentence: the verdict line says it and the evidence shows it.
  const explanation = differs ? spokenReply || 'Some details differ from the note.' : '';

  return (
    <Stack gap="sm">
      {[...audioUrls].map(([fileId, url]) => (
        <audio
          key={fileId}
          ref={playback.registerAudio(fileId)}
          src={url}
          preload="metadata"
          onTimeUpdate={playback.handleTimeUpdate}
          onEnded={playback.handleEnded}
          onError={playback.handleError}
        />
      ))}
      <VerdictLine
        text={headline ?? (differs ? 'Differs from the source' : 'Confirmed')}
        dark={dark}
      />
      {explanation && (
        <Text size="textSm" c={dark ? 'gray.3' : 'gray.7'}>
          {explanation}
        </Text>
      )}
      {primary && <PrimaryEvidence group={primary} playback={playback} dark={dark} />}
      {differs && (
        <VerifyOffer
          label="Would you like me to propose an edit based on this?"
          dark={dark}
          onClick={onAction}
        />
      )}
    </Stack>
  );
}

type EvidenceGroup =
  | { kind: 'transcript'; items: TranscriptEvidence[] }
  | { kind: 'document'; items: DocumentEvidence[] }
  | { kind: 'client_record'; items: ClientRecordEvidence[] };

/**
 * Only the source named in the verdict line is shown, with every passage it contributed — a claim
 * often spans several segments or paragraphs. Grouping by kind instead would show every attached
 * file on a suitability report, where all of them are documents.
 */
function primaryEvidence(evidence: VerifyEvidence[]): EvidenceGroup | null {
  const first = evidence[0];
  if (!first) return null;
  const items = evidence.filter(item => sourceKey(item) === sourceKey(first));
  if (first.kind === 'transcript') return { kind: 'transcript', items: items.filter(isTranscript) };
  if (first.kind === 'document') return { kind: 'document', items: items.filter(isDocument) };
  return { kind: 'client_record', items: items.filter(isClientRecord) };
}

function sourceKey(item: VerifyEvidence): string {
  if (item.kind === 'transcript') return `recording:${item.fileId ?? ''}`;
  if (item.kind === 'document') return `document:${item.file}`;
  return 'records';
}

const isTranscript = (item: VerifyEvidence): item is TranscriptEvidence =>
  item.kind === 'transcript';
const isDocument = (item: VerifyEvidence): item is DocumentEvidence => item.kind === 'document';
const isClientRecord = (item: VerifyEvidence): item is ClientRecordEvidence =>
  item.kind === 'client_record';

function PrimaryEvidence({
  group,
  playback,
  dark,
}: {
  group: EvidenceGroup;
  playback: ReturnType<typeof useSegmentPlayback>;
  dark?: boolean;
}) {
  if (group.kind === 'transcript') {
    return <TranscriptCards items={group.items} playback={playback} />;
  }
  if (group.kind === 'document') {
    return <DocumentLines items={group.items} dark={dark} />;
  }
  return <RecordLines items={group.items} dark={dark} />;
}

/** Segment ids repeat across recordings, so a card is identified by both. */
function segmentKey(item: TranscriptEvidence): string {
  return `${item.fileId ?? 'unknown'}:${item.segmentId}`;
}

function TranscriptCards({
  items,
  playback,
}: {
  items: TranscriptEvidence[];
  playback: ReturnType<typeof useSegmentPlayback>;
}) {
  return (
    <>
      {items.map(item => (
        <AudioTranscriptCard
          key={segmentKey(item)}
          recordingName={item.recordingName}
          recordingDate={item.recordingDate}
          segment={{
            id: item.segmentId,
            speaker: item.speaker,
            text: item.text,
            startSeconds: item.startSeconds,
            endSeconds: item.endSeconds,
          }}
          isPlaying={playback.isPlaying(segmentKey(item))}
          progress={playback.progressFor(segmentKey(item))}
          onTogglePlay={() =>
            playback.toggle({
              key: segmentKey(item),
              fileId: item.fileId ?? '',
              startSeconds: item.startSeconds,
              endSeconds: item.endSeconds,
            })
          }
        />
      ))}
    </>
  );
}

/**
 * The document's own words, set behind a rule — the same marker the transcript card uses for a
 * recording's words. Two kinds of text never get the rule: a client record, which is the model's
 * restatement rather than anything the source says, and a quote from a client file, which is
 * checked only against a filename we hold and so is unverified prose.
 */
function DocumentLines({ items, dark }: { items: DocumentEvidence[]; dark?: boolean }) {
  const verified = items.filter(item => item.located !== false);
  const unverified = items.filter(item => item.located === false);
  const colour = dark ? 'gray.2' : 'gray.8';
  return (
    <>
      {verified.length > 0 && (
        <Stack
          gap="xs"
          className={`${classes.sourceQuotes} ${dark ? classes.sourceQuotesDark : ''}`}
        >
          <VisuallyHidden>Quoted from the source</VisuallyHidden>
          {verified.map((item, index) => (
            <Fragment key={`${item.file}:${index}`}>
              {index > 0 && (
                <Text size="textSm" c={dark ? 'gray.5' : 'gray.6'} aria-hidden>
                  ⋯
                </Text>
              )}
              <Text size="textSm" c={colour} className={classes.quoteLine}>
                {plainQuote(item.quote)}
              </Text>
            </Fragment>
          ))}
        </Stack>
      )}
      {unverified.map((item, index) => (
        <Text key={`${item.file}:${index}`} size="textSm" c={colour} className={classes.quoteLine}>
          {plainQuote(item.quote)}
        </Text>
      ))}
    </>
  );
}

function RecordLines({ items, dark }: { items: ClientRecordEvidence[]; dark?: boolean }) {
  const withClientPrefix = new Set(items.map(item => item.client)).size > 1;
  return (
    <>
      {items.map((item, index) => (
        <Text
          key={`${item.client}:${index}`}
          size="textSm"
          c={dark ? 'gray.2' : 'gray.8'}
          className={classes.quoteLine}
        >
          {withClientPrefix && (
            <Text span fw={FONT_WEIGHTS.semibold}>
              {item.client}:{' '}
            </Text>
          )}
          {item.text}
        </Text>
      ))}
    </>
  );
}
